import type { BtlAddress, BtlApplication, UpdateCaseDto } from 'api/btlApplicationForm/types';
import { PropertyTenure, RefinancePurpose } from 'api/btlApplicationForm/types';
import type { ExistingFirmResponse } from 'api/firm/firm.types';
import { mapFormSubmissionFunnelTypeToApi } from 'pages/ApplicationFormPage/Submission/submissionRouteHelpers';
import { transformAddress, hasCompleteAddressEntry } from '../common/Address';
import { buildIntroducerPayloadFromFormValues } from './btlIntroducerHelpers';

interface BuildUpdateFinanceAndPropertyDtoParams {
  caseId: string;
  propertyId?: string;
  mergedValues: Record<string, any>;
  data?: BtlApplication;
  firm?: ExistingFirmResponse | null;
}

function normalizeFeesAddedToBalance(value: unknown, fallback: boolean): boolean {
  if (Array.isArray(value)) {
    return value[0] ?? false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return fallback;
}

function normalizeTermInMonths(value: unknown, fallbackMonths: number): number {
  if (typeof value !== 'number') {
    return fallbackMonths;
  }

  // Hydrated form values are in years; submitted form values are transformed to months.
  if (value <= 40) {
    return value * 12;
  }

  return value;
}

function normalizeCheckboxValue(value: unknown): boolean | null | undefined {
  if (Array.isArray(value)) {
    return value[0] ?? false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return undefined;
}

/**
 * Backend accepts `address: null` for EXPECTED properties and preserves the existing
 * address in the domain model — see strideup-be case.e2e.ts finance-and-property tests.
 */
function resolvePropertyAddress(mergedValues: Record<string, any>): BtlAddress | null {
  if (!hasCompleteAddressEntry(mergedValues)) {
    return null;
  }

  return transformAddress(mergedValues, true);
}

function omitUndefined<T extends Record<string, unknown>>(value: T): Partial<T> {
  return Object.fromEntries(Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined)) as Partial<T>;
}

export function buildUpdateFinanceAndPropertyDto({
  caseId,
  propertyId,
  mergedValues,
  data,
  firm,
}: BuildUpdateFinanceAndPropertyDtoParams): UpdateCaseDto {
  const existingProperty = data?.applicationProperty?.property;

  const purpose = mergedValues.purpose ?? data?.purpose;
  const refinancePurpose =
    purpose === 'PURCHASE' ? null : mergedValues.refinancePurpose ?? data?.refinancePurpose ?? null;
  const financingAmount = mergedValues.financingAmount ?? data?.financingAmount;
  const paymentProfile = mergedValues.paymentProfile ?? data?.paymentProfile ?? null;
  const resolvedPropertyType = mergedValues.propertyType ?? existingProperty?.propertyType;
  const resolvedHasCommunalSpace =
    normalizeCheckboxValue(mergedValues.hasCommunalSpace) ?? existingProperty?.attributes?.hasCommunalSpace ?? null;

  const outstandingBalance =
    refinancePurpose === RefinancePurpose.LIKE_FOR_LIKE
      ? financingAmount
      : mergedValues.outstandingBalance ?? data?.outstandingBalance ?? null;

  const property = omitUndefined({
    purchasePrice: mergedValues.purchasePrice ?? existingProperty?.purchasePrice ?? 0,
    grossMonthlyRentalPayment:
      mergedValues.grossMonthlyRentalPayment ?? existingProperty?.grossMonthlyRentalPayment ?? 0,
    address: resolvePropertyAddress(mergedValues),
    propertyType: resolvedPropertyType || undefined,
    propertySubType: mergedValues.propertySubType ?? existingProperty?.propertySubType ?? undefined,
    expectedPropertyType: mergedValues.expectedPropertyType ?? existingProperty?.expectedPropertyType ?? undefined,
    attributes:
      resolvedPropertyType === 'HMO'
        ? {
            numberOfBedrooms: mergedValues.numberOfBedrooms ?? existingProperty?.attributes?.numberOfBedrooms ?? null,
            hasCommunalSpace: resolvedHasCommunalSpace,
          }
        : resolvedPropertyType === 'MUB'
        ? { numberOfUnits: mergedValues.numberOfUnits ?? existingProperty?.attributes?.numberOfUnits ?? null }
        : undefined,
  });

  const propertyData = omitUndefined({
    property,
    tenure: mergedValues.tenure as PropertyTenure | undefined,
    yearsRemainingOnLease: mergedValues.yearsRemainingOnLease,
    estateAgent: mergedValues.estateAgent,
  });

  const submissionFunnelType = mapFormSubmissionFunnelTypeToApi(mergedValues.submissionFunnelType);
  const introducer = firm ? buildIntroducerPayloadFromFormValues(mergedValues, firm) : undefined;

  return omitUndefined({
    caseId,
    propertyId: propertyId ?? existingProperty?.id,
    financeData: {
      purpose,
      refinancePurpose,
      feesAddedToBalance: normalizeFeesAddedToBalance(
        mergedValues.feesAddedToBalance,
        data?.feesAddedToBalance ?? false,
      ),
      productId: data?.product?.id ?? null,
      termInMonths: normalizeTermInMonths(mergedValues.termInMonths, data?.termInMonths ?? 0),
      depositAmount: data?.depositAmount ?? null,
      financingAmount,
      outstandingBalance: purpose === 'PURCHASE' ? null : outstandingBalance,
      paymentProfile,
      repaymentStrategy: mergedValues.repaymentStrategy ?? null,
      repaymentStrategyDetails: mergedValues.repaymentStrategyDetails ?? null,
    },
    propertyData,
    submissionFunnelType,
    introducer,
  }) as UpdateCaseDto;
}
